/**
 * Runtime language selection for the Direct Sale flow.
 *
 * DesignDeck ships as a single static bundle embedded by many shops (see
 * src/lib/theme.ts for the same constraint applied to the brand colour), so
 * the language cannot be a build-time choice. It arrives on the iframe URL as
 * `?lang=<code>` and is applied before the first render, mirroring
 * applyBranding — otherwise the customer sees the default language flash
 * before their own.
 *
 * In the Seller iframe the auto-login redirect drops `?lang=` from the URL,
 * so the value recorded at the start (lib/iframe-boot.ts) stands in when the
 * URL has none. Direct Sale keeps its URL and never reads that record — a
 * Seller embed on the same page must not leak its language into it.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
  parseLanguageTag,
  type ParsedLanguage,
  type SupportedLanguage,
} from '@designdeck/shared';
import de from '@/locales/de.json';
import en from '@/locales/en.json';
import fr from '@/locales/fr.json';
import es from '@/locales/es.json';
import it from '@/locales/it.json';
import nl from '@/locales/nl.json';
import pt from '@/locales/pt.json';
import pl from '@/locales/pl.json';
import { logger } from '@/utils/logger';
import { isDirectSalePath, readIframeParams } from './iframe-boot';

// The language list lives in the shared package so the API and the MCP
// tools decide "supported" with the same switch; re-exported here for the
// web's existing consumers.
export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, isSupportedLanguage };
export type { SupportedLanguage };

/** The last resolved tag, kept so `Intl` can format with the full locale. */
let resolved: ParsedLanguage | null = null;
/** Codes already reported, so a misconfigured embed logs once, not per request. */
const warnedCodes = new Set<string>();

/**
 * Parses a tag and, when a shop sent something we do not support, says so
 * once in the console — a misconfigured embed is otherwise invisible
 * because the page still works, just in German.
 */
function resolveTag(
  raw: string | null | undefined,
  source: string
): ParsedLanguage | null {
  const parsed = parseLanguageTag(raw);
  if (!parsed && raw && !warnedCodes.has(raw)) {
    warnedCodes.add(raw);
    logger.warn(
      'i18n',
      `Unsupported language "${raw}" from ${source}, falling back to ${DEFAULT_LANGUAGE}`
    );
  }
  return parsed;
}

/**
 * Reads `?lang=` from the current URL, falling back to the value recorded at
 * the Seller iframe start when the URL has none (never on Direct Sale).
 * Falls back silently rather than failing — an embedder sending an
 * unsupported code should still get a usable page.
 */
export function getIframeLanguage(): SupportedLanguage {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

  const raw = new URLSearchParams(window.location.search).get('lang');
  if (raw) {
    resolved = resolveTag(raw, 'the URL');
  } else if (isDirectSalePath(window.location.pathname)) {
    resolved = null;
  } else {
    resolved = resolveTag(readIframeParams().lang, 'the iframe start');
  }

  return resolved?.language ?? DEFAULT_LANGUAGE;
}

/**
 * The full locale for `Intl` (`pt-BR`, `en-GB`), as resolved by the last
 * `getIframeLanguage()` call; the base language when no region was sent.
 */
export function getIntlLocale(): string {
  return resolved?.locale ?? DEFAULT_LANGUAGE;
}

/**
 * Mirrors the active language onto <html lang>, so screen readers,
 * hyphenation and `Intl`-driven browser features follow the UI language.
 */
function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
}

/** Initializes i18next. Safe to call more than once. */
export function initI18n(language: SupportedLanguage = getIframeLanguage()) {
  if (i18n.isInitialized) {
    void i18n.changeLanguage(language);
    syncDocumentLanguage(language);
    return i18n;
  }

  // Registered once, before init: covers this init and every later
  // changeLanguage, from wherever it is called.
  i18n.on('languageChanged', syncDocumentLanguage);

  void i18n.use(initReactI18next).init({
    resources: {
      de: { translation: de },
      en: { translation: en },
      fr: { translation: fr },
      es: { translation: es },
      it: { translation: it },
      nl: { translation: nl },
      pt: { translation: pt },
      pl: { translation: pl },
    },
    lng: language,
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      // React already escapes rendered values.
      escapeValue: false,
    },
    // A missing key renders its own key rather than an empty string, so a
    // gap is visible in review instead of silently blanking the UI.
    parseMissingKeyHandler: (key) => key,
  });
  syncDocumentLanguage(language);

  return i18n;
}

export default i18n;
